import React, { useRef } from "react";
import { FormLayout, Banner, Form, Button } from "@shopify/polaris";
import { useTranslation } from "react-i18next";

interface DocumentFormShellProps {
  onSubmit: () => void | Promise<void>;
  isLoading: boolean;
  isFailed: boolean;
  /** Display-ready text (already translated) for why it failed — falls back
   * to the generic "error" banner when not given, as before. */
  failureMessage?: string | null;
  isSuccessful: boolean;
  /** Display-ready text for the success banner — "ok" when not given. */
  successMessage?: string | null;
  submitDisabled?: boolean;
  children: React.ReactNode;
}

export default function DocumentFormShell({
  onSubmit,
  isLoading,
  isFailed,
  failureMessage,
  isSuccessful,
  successMessage,
  submitDisabled,
  children,
}: DocumentFormShellProps) {
  const { t } = useTranslation();
  // A ref, not state: two fast clicks (or Enter) land before a re-render, and
  // both would otherwise pass the check. Polaris' <Form> also submits on Enter
  // through a hidden button that is never disabled, so the visible Button's
  // loading/disabled state alone cannot stop a second request.
  const inFlight = useRef(false);

  const handleSubmit = async () => {
    // After success the modal is only waiting to close; a further submit
    // would create the same document again.
    if (inFlight.current || isSuccessful) return;
    inFlight.current = true;
    try {
      await onSubmit();
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <div className="user-form">
      <Form onSubmit={handleSubmit}>
        <div className="form-warnings">
          {isFailed && (
            <div>
              <Banner tone="critical">{failureMessage || t("error")}</Banner>
            </div>
          )}
          {isSuccessful && (
            <div>
              <Banner tone="info">{successMessage || t("ok")}</Banner>
            </div>
          )}
        </div>
        <FormLayout>
          {children}
          <Button
            loading={isLoading}
            disabled={submitDisabled || isSuccessful}
            submit
          >
            {t("save")}
          </Button>
        </FormLayout>
      </Form>
    </div>
  );
}
